import { useQueryClient } from 'react-query';
import { useState, useRef } from 'react';
import { Brief, getNewBrief } from '@fiverr-private/ai_brief';
import { stats } from '@fiverr-private/obs';
import { useBriefCreationContext } from '../context/BriefCreationContext';
import { METRICS_PREFIX, Step } from '../constants';
import { IntentClassification, generateBrief } from '../api';
import { reportTimeMetric } from '../monitoring';
import { useCustomSections } from '../components';
import { logger } from '../utils/logger';
import { InvalidIntent, invalidIntents } from '../i18n';
import { generateBriefQueryKey, pollGenerationQueryKey, UNABLE_TO_PRODUCE_A_BRIEF } from './constants';
import { QUERY_RETRY_DELAY_MS, QUERY_TIMEOUT_MS } from './classification';
import { isInvalidIntent } from './classification/utils';

const generateBriefRequestType = 'generate_brief';
const BRIEF_TYPE = 'dynamic_matching_person';

export const useGeneratePersonBrief = () => {
    const { setStep, contextId, source, userInput, setBrief, setIntentClassification } = useBriefCreationContext();
    const { addCustomSections } = useCustomSections();
    const queryClient = useQueryClient();

    const [isLoading, setIsLoading] = useState(false);
    const [hasTimedOut, setHasTimedOut] = useState(false);
    const onIntentClassifiedCallbackRef = useRef<((intent: IntentClassification) => void) | undefined>(undefined);

    const handleGenerationError = (error?: Error) => {
        logger.error(new Error('Failed while generating brief'), { userInput, cause: error });
        setStep((prevStep: Step) => (prevStep === Step.Creation ? Step.Error : prevStep));
        stats.count(`${METRICS_PREFIX}.${generateBriefRequestType}`, `error`);
    };

    const handleRephraseWarning = (error?: Error) => {
        logger.warn(new Error('Insufficient information'), { cause: error });
        setStep((prevStep: Step) => (prevStep === Step.Creation ? Step.Rephrase : prevStep));
        stats.count(`${METRICS_PREFIX}.${generateBriefRequestType}`, `rephrase`);
    };

    const handleGenerationResult = (generatedBrief: Brief) => {
        if (!generatedBrief) {
            handleGenerationError(new Error('Failed to generate brief'));
            return;
        }

        const briefWithCustomSections = addCustomSections({ brief: generatedBrief });
        setBrief((prevBrief) =>
            prevBrief ? { ...prevBrief, sections: briefWithCustomSections?.sections || [] } : generatedBrief
        );
        stats.count(`${METRICS_PREFIX}.${generateBriefRequestType}`, `success`);
    };

    const generatePersonBrief = async ({
        userInput,
        onIntentClassified,
        abortController,
    }: {
        userInput: string;
        onIntentClassified?: (intentClassification: IntentClassification) => void;
        abortController: AbortController;
    }): Promise<Brief | undefined> => {
        const generationTimeStart = Date.now();
        setIsLoading(true);
        setHasTimedOut(false);
        onIntentClassifiedCallbackRef.current = onIntentClassified;

        let finalBrief: Brief | undefined;
        let onIntentClassifiedCalled = false;

        const overallTimeoutId = setTimeout(() => {
            setHasTimedOut(true);
        }, QUERY_TIMEOUT_MS);

        try {
            const { data: generationPollingId } = await queryClient.fetchQuery<{ data: string }>({
                queryKey: [generateBriefQueryKey, userInput],
                queryFn: () => generateBrief({ userInput, contextId, source, briefType: BRIEF_TYPE }),
                cacheTime: 0,
                retry: 2,
                ...(abortController && { signal: abortController.signal }),
            });

            if (!generationPollingId) {
                handleGenerationError(new Error('No polling ID received from generation request.'));
                return;
            }

            let pollAttempts = 0;
            const maxPollAttempts = Math.ceil(QUERY_TIMEOUT_MS / QUERY_RETRY_DELAY_MS);
            let briefIsComplete = false;
            let currentBriefData: Brief | undefined;

            while (
                pollAttempts < maxPollAttempts &&
                !briefIsComplete &&
                !hasTimedOut &&
                !abortController.signal.aborted
            ) {
                try {
                    currentBriefData = await queryClient.fetchQuery<Brief>({
                        queryKey: [pollGenerationQueryKey, generationPollingId, userInput],
                        queryFn: () => getNewBrief(generationPollingId, source || '', 'dynamic_matching', BRIEF_TYPE),
                        staleTime: 0,
                        retry: false,
                        cacheTime: 0,
                        ...(abortController && { signal: abortController.signal }),
                    });

                    const generationInvalidIntent =
                        currentBriefData?.error && invalidIntents.includes(currentBriefData.error as InvalidIntent)
                            ? (currentBriefData.error as IntentClassification)
                            : undefined;

                    const intent = (currentBriefData?.intentClassification ?? generationInvalidIntent) as
                        | IntentClassification
                        | undefined;

                    if (intent && !onIntentClassifiedCalled) {
                        setIntentClassification(intent);
                        onIntentClassifiedCallbackRef.current?.(intent);
                        onIntentClassifiedCalled = true;

                        if (generationInvalidIntent || isInvalidIntent(intent)) {
                            clearTimeout(overallTimeoutId);
                            return;
                        }
                    }

                    if (currentBriefData?.sections?.length > 0) {
                        briefIsComplete = true;
                        finalBrief = currentBriefData;
                        break;
                    } else if (currentBriefData?.error === UNABLE_TO_PRODUCE_A_BRIEF) {
                        handleRephraseWarning(new Error(currentBriefData.error));
                        break;
                    } else if (currentBriefData?.error) {
                        handleGenerationError(new Error(currentBriefData.error));
                        break;
                    }

                    if (!briefIsComplete) {
                        await new Promise((resolve) => setTimeout(resolve, QUERY_RETRY_DELAY_MS));
                    }
                } catch (pollError) {
                    await new Promise((resolve) => setTimeout(resolve, QUERY_RETRY_DELAY_MS));
                }

                pollAttempts++;
            }

            clearTimeout(overallTimeoutId);

            if (abortController.signal.aborted) {
                return;
            }

            if (hasTimedOut) {
                handleGenerationError(new Error('Brief generation timed out after polling attempts.'));
            } else if (!briefIsComplete) {
                handleGenerationError(new Error('Brief generation failed: did not complete after all attempts.'));
            } else if (finalBrief) {
                handleGenerationResult(finalBrief);
                reportTimeMetric({
                    metricName: 'extract_and_generate_brief',
                    time: Date.now() - generationTimeStart,
                });
                return finalBrief;
            }
        } catch (error) {
            clearTimeout(overallTimeoutId);
            handleGenerationError(error as Error);
            return;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        generatePersonBrief,
    };
};
